import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getEstimate, verifyEstimatorPassword } from "@/lib/estimator.functions";
import { getStoredPassword, setStoredPassword } from "@/lib/estimator-session";
import { ArrowLeft, ArrowRight, Download, Lock, Image as ImageIcon } from "lucide-react";

export const Route = createFileRoute("/estimator_/blueprint/$id")({
  head: () => ({
    meta: [
      { title: "Blueprint | Apex AV" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BlueprintViewerPage,
});

type BlueprintPage = { label: string; image: string; width: number; height: number };
type Blueprint = { pages: BlueprintPage[]; pageCount: number; pushedAt: string };

function BlueprintViewerPage() {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && getStoredPassword()) setUnlocked(true);
  }, []);
  return unlocked ? <Viewer /> : <PasswordGate onUnlock={() => setUnlocked(true)} />;
}

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const verify = useServerFn(verifyEstimatorPassword);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await verify({ data: { password } });
      if (res.ok) {
        setStoredPassword(password);
        onUnlock();
      } else setError("Incorrect password.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="container mx-auto px-6 py-24 flex items-center justify-center min-h-[70vh]">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-border bg-card p-10 shadow-card">
        <div className="h-12 w-12 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
          <Lock className="h-6 w-6 text-brand-foreground" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold">Blueprint</h1>
        <p className="mt-2 text-sm text-muted-foreground">Enter the estimator password to view.</p>
        <input
          type="password"
          aria-label="Password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-6 w-full rounded-md border border-border bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand"
          placeholder="Password"
        />
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
        >
          {loading ? "Verifying…" : "Unlock"} <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </section>
  );
}

function Viewer() {
  const { id } = Route.useParams();
  const getEstFn = useServerFn(getEstimate);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [clientName, setClientName] = useState<string | null>(null);
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null);

  useEffect(() => {
    const pw = getStoredPassword();
    if (!pw) return;
    setLoading(true);
    setError(null);
    getEstFn({ data: { password: pw, id } })
      .then((r) => {
        const est = r.estimate as any;
        setName(est.name);
        setClientName(est.client_name ?? null);
        setBlueprint((est.data?.blueprint as Blueprint | undefined) ?? null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load."))
      .finally(() => setLoading(false));
  }, [id, getEstFn]);

  function downloadPage(p: BlueprintPage, i: number) {
    const a = document.createElement("a");
    a.download = `${name || "blueprint"}-page-${i + 1}.jpg`;
    a.href = p.image;
    a.click();
  }

  return (
    <section className="container mx-auto px-6 pt-16 pb-32">
      <Link
        to="/estimator/saved"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to saved estimates
      </Link>
      <div className="mt-6 flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
          <ImageIcon className="h-5 w-5 text-brand-foreground" />
        </div>
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold">{name || "Blueprint"}</h1>
          {clientName && <p className="text-sm text-muted-foreground">{clientName}</p>}
        </div>
        <Link
          to="/estimator"
          search={{ id }}
          className="ml-auto inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
        >
          Open estimate <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {loading && <p className="mt-8 text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="mt-8 text-sm text-destructive">{error}</p>}

      {!loading && !error && (!blueprint || blueprint.pages.length === 0) && (
        <div className="mt-8 rounded-xl border border-border bg-card p-12 text-center text-muted-foreground shadow-card">
          No blueprint was saved with this estimate.
        </div>
      )}

      {blueprint && blueprint.pages.length > 0 && (
        <div className="mt-8 space-y-6">
          <p className="text-xs text-muted-foreground">
            Saved {new Date(blueprint.pushedAt).toLocaleString()} · {blueprint.pageCount} page
            {blueprint.pageCount === 1 ? "" : "s"}
          </p>
          {blueprint.pages.map((p, i) => (
            <div key={i} className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <div className="text-sm font-medium">{p.label}</div>
                <button
                  onClick={() => downloadPage(p, i)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
              </div>
              <div className="p-2 bg-muted/30 overflow-auto">
                <img src={p.image} alt={p.label} className="max-w-full h-auto mx-auto" />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
